import type { ICalculateAdjustmentsRequest, ICalculateAdjustmentsResponse, IQualityAdjustmentResult } from '@local/lib';

import { CalculateAdjustmentsRequestSchema, isQuantityFormulaType, QualityAdjustmentType } from '@local/lib';

// Simulated network latency so the "Test" button's loading state is still visible — the real
// endpoint usually takes a few hundred ms.
const DUMMY_LATENCY_MS = 400;

/**
 * Produces a plausible (not mathematically accurate) result for one spec, just enough for the
 * Quality Formulas playground to render a coherent Step 3 result card.
 */
const buildDummyAdjustmentResult = (
  specType: string,
  input: ICalculateAdjustmentsRequest['qualityInputs'][keyof ICalculateAdjustmentsRequest['qualityInputs']],
): IQualityAdjustmentResult => {
  if (!input) throw new Error(`Missing quality input for spec type ${specType}`);

  const adjustmentType = isQuantityFormulaType(input.formulaType)
    ? QualityAdjustmentType.Quantity
    : QualityAdjustmentType.Price;

  return {
    actualSpec: input.actualSpec,
    adjustmentType,
    description: `${specType} Adjustment (${input.actualSpec})`,
    matchedTierIndex: null,
    priceAdjustmentPerUnit: adjustmentType === QualityAdjustmentType.Price ? 0 : null,
    priceAdjustmentTotal: adjustmentType === QualityAdjustmentType.Price ? 0 : null,
  };
};

/**
 * Dummy stand-in for the real Pricing Engine quality-adjustment calculation endpoint.
 *
 * The Contract Entry API deliberately does not (and will not) expose a `/calculate` endpoint —
 * the UI only ever authenticates against the Contract Entry API. Calculating quality adjustments
 * will eventually require direct UI access to the ASH Pricing Engine API; until that access
 * exists, this returns a locally-built, zero-adjustment response (validated against the same
 * shared Zod schema the real endpoint uses) so the rest of the Quality Formulas playground
 * (results mapping, summary, notifications) keeps working end-to-end against a realistic shape.
 */
export const calculateQualityAdjustments = async (
  request: ICalculateAdjustmentsRequest,
): Promise<ICalculateAdjustmentsResponse> => {
  const validatedRequest = CalculateAdjustmentsRequestSchema.parse(request);

  await new Promise(resolve => {
    setTimeout(resolve, DUMMY_LATENCY_MS);
  });

  const adjustments: ICalculateAdjustmentsResponse['adjustments'] = {};
  for (const [specType, input] of Object.entries(validatedRequest.qualityInputs)) {
    // `specType` is already one of `SpecType`'s own values (it came from iterating
    // `validatedRequest.qualityInputs`, a `Partial<Record<SpecType, ...>>`) — this cast back to a
    // key of that same record is a known pre-existing false positive under this monorepo's typed
    // lint project resolution for workspace-package enums (see also the ported reference-app
    // files under pages/quality-formulas and pages/templates hitting the same quirk).
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
    adjustments[specType as keyof typeof adjustments] = buildDummyAdjustmentResult(specType, input);
  }

  return {
    // No real quantity-adjustment math is performed in this dummy — echoes the shipped quantity
    // unchanged, same as a request with no active quantity-type spec would.
    adjustedInvoiceQuantity: validatedRequest.shippedQuantity,
    adjustments,
    shippedQuantity: validatedRequest.shippedQuantity,
    tradePrice: validatedRequest.tradePrice,
  };
};
